import { forwardRef, type HTMLProps } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import { useTableContext } from '../../hooks/useTableContext';
import { TableContextProvider } from '../TableContextProvider';

type TableHeadProps = Omit<HTMLProps<HTMLTableSectionElement>, 'className'>;

const tableHeadVariants = variants({
  base: cn('text-left text-body-sm leading-tight'),
  variants: {
    stickyHeader: {
      true: cn(
        'sticky',
        'top-[calc(var(--sticky-header-height)+var(--spacing-sm))]',
        'z-10',
        'will-change-[top]',
      ),
    },
  },
});

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ children, ...props }, ref) => {
    const { level, size, spacing, stickyHeader } = useTableContext();

    return (
      <TableContextProvider
        level={level}
        size={size}
        spacing={spacing}
        stickyHeader={stickyHeader}
        variant="default"
      >
        <thead
          ref={ref}
          className={tableHeadVariants({ stickyHeader })}
          {...props}
        >
          {children}
        </thead>
      </TableContextProvider>
    );
  },
);

TableHead.displayName = 'TableHead';
